import React from "react";
import { Link } from "react-router-dom";
import { Download } from "lucide-react";
import { toast } from "sonner";
import Footer from "../components/Footer";
import { useAuth } from "../context/AuthContext";
import api from "../lib/apiClient";

// Download-my-data button — Iter-58.8 (GDPR Art. 15 / 20 + CCPA right
// to know). Hits /api/users/me/export, which returns a JSON Blob the
// browser auto-saves. Wired here on the Privacy page so the legal
// notice and the self-service control live in one place. Rate-limited
// server-side (3/hour/user) so a logged-in attacker can't scrape.
const DownloadMyDataButton = () => {
  const { token, user } = useAuth();
  const [busy, setBusy] = React.useState(false);
  if (!token || !user) {
    return (
      <p className="text-gray-400 text-sm" data-testid="gdpr-export-anon-notice">
        <Link to="/login" className="text-emerald-400">Sign in</Link> to download
        a copy of all your personal data.
      </p>
    );
  }
  const handleClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await api.get("/users/me/export", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `globaleyes-export-${user.username || "me"}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Free the blob URL after the click hands off to the browser's
      // download manager (a 1s timer is the conventional safe window).
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Your data export downloaded.");
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) {
        toast.error("Too many exports. Try again in an hour.");
      } else {
        toast.error("Export failed. Please contact support.");
      }
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      data-testid="gdpr-export-btn"
      className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 disabled:bg-gray-700 disabled:cursor-not-allowed text-white text-sm font-medium px-4 py-2 rounded transition-colors"
    >
      <Download className="h-4 w-4" />
      {busy ? "Preparing your export\u2026" : "Download my data (.json)"}
    </button>
  );
};

// Delete-my-account button — Iter-58.9 (GDPR Art. 17 / CCPA right to
// delete). Two-step confirm: click "Delete my account" → reveal a
// type-your-username confirmation field → POST /api/users/me/delete.
// Backend wipes PII, marks user/channels/videos as deleted, scrubs
// comment text, cancels open offers. Irreversible.
const DeleteMyAccountButton = () => {
  const { token, user, logout } = useAuth();
  const [stage, setStage] = React.useState("idle"); // idle | confirming | busy
  const [confirmText, setConfirmText] = React.useState("");
  if (!token || !user) return null;
  const expected = (user.username || "").toLowerCase();
  const canSubmit = confirmText.trim().toLowerCase() === expected;
  const submit = async () => {
    if (!canSubmit || stage === "busy") return;
    setStage("busy");
    try {
      await api.post("/users/me/delete", { confirm: confirmText.trim() });
      toast.success("Account deleted. Signing you out\u2026");
      setTimeout(() => {
        try { logout?.(); } catch (_e) { /* noop */ }
        window.location.href = "/";
      }, 1500);
    } catch (err) {
      const status = err.response?.status;
      if (status === 422) {
        toast.error("Username didn't match. Try again.");
      } else if (status === 429) {
        toast.error("Too many attempts. Try again shortly.");
      } else {
        toast.error("Deletion failed. Please contact support.");
      }
      setStage("confirming");
    }
  };
  if (stage === "idle") {
    return (
      <button
        type="button"
        onClick={() => setStage("confirming")}
        data-testid="gdpr-delete-btn"
        className="inline-flex items-center gap-2 bg-rose-900/40 hover:bg-rose-800/60 border border-rose-500/30 text-rose-200 text-sm font-medium px-4 py-2 rounded transition-colors"
      >
        Delete my account\u2026
      </button>
    );
  }
  return (
    <div className="space-y-2" data-testid="gdpr-delete-confirm-box">
      <p className="text-rose-200 text-sm">
        This is <strong>permanent</strong>. Your videos will disappear from
        the site, your username will be released, and the data export
        download will stop working. Type your username
        (<code className="text-amber-300">{user.username}</code>) to confirm.
      </p>
      <input
        type="text"
        value={confirmText}
        onChange={(e) => setConfirmText(e.target.value)}
        placeholder={user.username}
        autoComplete="off"
        data-testid="gdpr-delete-confirm-input"
        className="w-full max-w-xs bg-gray-900 border border-rose-500/40 text-white text-sm px-3 py-2 rounded"
      />
      <div className="flex gap-2">
        <button
          type="button"
          onClick={submit}
          disabled={!canSubmit || stage === "busy"}
          data-testid="gdpr-delete-confirm-btn"
          className="bg-rose-600 hover:bg-rose-500 disabled:bg-gray-700 disabled:cursor-not-allowed text-white text-sm font-bold px-4 py-2 rounded transition-colors"
        >
          {stage === "busy" ? "Deleting\u2026" : "Permanently delete"}
        </button>
        <button
          type="button"
          onClick={() => { setStage("idle"); setConfirmText(""); }}
          className="bg-gray-800 hover:bg-gray-700 text-gray-200 text-sm px-4 py-2 rounded"
          data-testid="gdpr-delete-cancel-btn"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

// Privacy policy — drafted for NETBINGE, LLC running GlobalEyes.TV. This is a
// starting baseline; have an attorney review before scaling beyond MVP.
const PrivacyPage = () => (
  <div className="min-h-screen bg-gradient-to-b from-gray-950 to-black text-gray-200 flex flex-col" data-testid="privacy-page">
    <div className="container mx-auto px-4 py-12 max-w-3xl flex-1">
      <Link to="/" className="text-emerald-400 hover:text-emerald-300 text-sm">← Back home</Link>
      <h1 className="text-3xl sm:text-4xl font-bold text-white mt-4 mb-2">Privacy Policy</h1>
      <p className="text-gray-500 text-sm mb-8">Last updated: May 10, 2026 · NETBINGE, LLC</p>

      <div className="prose prose-invert prose-sm sm:prose-base max-w-none space-y-6">
        <section>
          <h2 className="text-xl text-white font-semibold">1. Who we are</h2>
          <p>
            GlobalEyes.TV is operated by <strong>NETBINGE, LLC</strong> ("we", "us", "our"). This Privacy Policy explains
            what information we collect when you use globaleyes.tv (the "Site") and how we handle it.
          </p>
        </section>

        <section>
          <h2 className="text-xl text-white font-semibold">2. Information we collect</h2>
          <ul className="list-disc ml-6 space-y-1">
            <li><strong>Account info</strong> — username, email address, and password (hashed) when you register.</li>
            <li><strong>Profile content</strong> — bio, profile image, sponsor links, and channel preferences you choose to share publicly.</li>
            <li><strong>Videos & captures</strong> — recordings you upload, plus the location metadata (city / country / lat-lng) you elect to attach.</li>
            <li><strong>Activity</strong> — videos you watch, applaud, comment on, share, gift, and commission.</li>
            <li><strong>Payment data</strong> — handled by <strong>Stripe</strong>. We never store full card numbers; we keep only the Stripe customer/transaction IDs needed to reconcile your account.</li>
            <li><strong>Device & log data</strong> — IP address, browser type, pages visited, and timestamps, for security and abuse prevention.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl text-white font-semibold">3. How we use your data</h2>
          <ul className="list-disc ml-6 space-y-1">
            <li>To deliver the service: render your dashboard, host your videos, route payments and refunds.</li>
            <li>To moderate the platform: detect spam, abuse, and policy violations.</li>
            <li>To communicate with you: account notifications, security alerts, and (only with your opt-in) promotional updates.</li>
            <li>To improve the product: aggregate, de-identified analytics on usage patterns.</li>
          </ul>
          <p className="mt-2">
            We do <strong>not</strong> sell your personal data. We do not run third-party advertising trackers.
          </p>
        </section>

        <section>
          <h2 className="text-xl text-white font-semibold">4. Third parties we share data with</h2>
          <ul className="list-disc ml-6 space-y-1">
            <li><strong>Stripe</strong> — to process payments, top-ups, subscriptions, gifts, commissions, and refunds.</li>
            <li><strong>Emergent</strong> — our hosting + object-storage provider for video files.</li>
            <li><strong>Law enforcement</strong> — only when compelled by valid legal process.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl text-white font-semibold">5. Your rights</h2>
          <p>You can:</p>
          <ul className="list-disc ml-6 space-y-1">
            <li>Edit or delete your videos and comments from your dashboard at any time.</li>
            <li>Request a copy of, or deletion of, your account data by emailing <Link to="/contact" className="text-emerald-400">our contact page</Link>.</li>
            <li>Cancel subscriptions through the Stripe customer portal (link inside your dashboard).</li>
          </ul>
          <p className="mt-2">
            EU/UK users have additional rights under GDPR (access, rectification, erasure, portability, restriction, objection). California
            users have rights under CCPA. Reach out to exercise any of these.
          </p>
        </section>

        <section>
          <h2 className="text-xl text-white font-semibold">6. Children</h2>
          <p>
            GlobalEyes.TV is not directed at children under 13. We do not knowingly collect personal data from anyone under 13. If you
            believe a child has registered, please contact us and we will delete the account.
          </p>
        </section>

        <section>
          <h2 className="text-xl text-white font-semibold">7. Data retention</h2>
          <p>
            We retain account and content data for as long as your account is active. Deleted videos are tombstoned for up to 30 days
            before being permanently purged. Transaction records are retained for 7 years for tax/accounting compliance.
          </p>
        </section>

        <section>
          <h2 className="text-xl text-white font-semibold">8. Security</h2>
          <p>
            Passwords are hashed with bcrypt. All traffic is encrypted with TLS. Stripe handles all card data under PCI-DSS Level 1.
            No system is perfect — if we detect a breach affecting you, we will notify you promptly.
          </p>
        </section>

        <section>
          <h2 className="text-xl text-white font-semibold">9. Changes to this policy</h2>
          <p>
            We may update this policy from time to time. Material changes will be announced on the Site. Continued use after such a
            change constitutes acceptance.
          </p>
        </section>

        <section data-testid="gdpr-export-section" className="border border-emerald-500/30 bg-emerald-950/20 rounded-lg p-4">
          <h2 className="text-xl text-white font-semibold mb-2">Your data, your copy</h2>
          <p className="mb-3">
            Under GDPR Article 15/20 and the CCPA, you can request a full copy
            of the personal data we hold for you at any time. One click below
            downloads a JSON file with your profile, channels, videos,
            comments, transactions, follow graph, and offer history.
          </p>
          <DownloadMyDataButton />
          <div className="mt-4 pt-4 border-t border-emerald-500/20">
            <h3 className="text-rose-300 font-semibold text-sm mb-1">Right to be forgotten</h3>
            <p className="text-gray-400 text-xs mb-2">
              Permanently deactivate your account, scrub your personal data, and
              hide your videos from the site. Stripe records (legal retention)
              and storage blobs (next sweep ~24h) are processed separately.
            </p>
            <DeleteMyAccountButton />
          </div>
        </section>

        <section>
          <h2 className="text-xl text-white font-semibold">10. Contact</h2>
          <p>
            Questions or requests? See our <Link to="/contact" className="text-emerald-400">Contact page</Link>.
          </p>
        </section>
      </div>
    </div>
    <Footer />
  </div>
);

export default PrivacyPage;
